import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { reportsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AssignmentIcon from '@mui/icons-material/Assignment';
import './Dashboard.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BarChartIcon from '@mui/icons-material/BarChart';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const COST_PER_REPORT = 0.08;
const PAGE_SIZE = 5;

const FILTER_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'yearly', label: 'Yearly' },
];

const FilterDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = FILTER_OPTIONS.find(o => o.key === value) || FILTER_OPTIONS[0];

  return (
    <div className="filter-dropdown" ref={ref}>
      <button type="button" className="filter-dropdown-btn" onClick={() => setOpen(o => !o)}>
        {current.label}
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} className={`fd-chevron ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className="filter-dropdown-menu">
          {FILTER_OPTIONS.map(o => (
            <div
              key={o.key}
              className={`fd-option ${o.key === value ? 'active' : ''}`}
              onClick={() => { onChange(o.key); setOpen(false); }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CombinedDonut = ({ completed, pending, total }) => {
  const [mounted, setMounted] = useState(false);
  const [hover, setHover] = useState(null); 
  const r = 89, circ = 2 * Math.PI * r;
  const compPct = total > 0 ? completed / total : 0;
  const pendPct = total > 0 ? pending / total : 0;
  const compDash = mounted ? circ * compPct : 0;
  const pendDash = mounted ? circ * pendPct : 0;

  const GAP_DEG = 2;
  const gapLenPx = (GAP_DEG / 360) * circ;
  const compVisible = Math.max(0, compDash - gapLenPx);
  const pendVisible = Math.max(0, pendDash - gapLenPx);
  const compRotate = -90 + GAP_DEG / 2;
  const pendRotate = -90 + compPct * 360 + GAP_DEG / 2;

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const showHover = (label, value) => (e) => {
    setHover({ label, value, x: e.clientX, y: e.clientY });
  };
  const moveHover = (label, value) => (e) => {
    setHover(h => (h ? { ...h, x: e.clientX, y: e.clientY } : h));
  };

  return (
    <div className="combined-donut-wrap">
      <svg width="230" height="230" viewBox="0 0 230 230" style={{overflow:'visible'}}>
        <circle cx="115" cy="115" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="38"/>
        <circle cx="115" cy="115" r={r} fill="none" stroke="#34d399" strokeWidth="38"
          strokeLinecap="butt"
          strokeDasharray={`${compVisible} ${circ - compVisible}`}
          strokeDashoffset="0"
          style={{transition:'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)', cursor:'pointer'}}
          transform={`rotate(${compRotate} 115 115)`}
          onMouseEnter={showHover('Completed', completed)}
          onMouseMove={moveHover('Completed', completed)}
          onMouseLeave={() => setHover(null)}/>
        <circle cx="115" cy="115" r={r} fill="none" stroke="#fcd34d" strokeWidth="38"
          strokeLinecap="butt"
          strokeDasharray={`${pendVisible} ${circ - pendVisible}`}
          strokeDashoffset="0"
          style={{transition:'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)', cursor:'pointer'}}
          transform={`rotate(${pendRotate} 115 115)`}
          onMouseEnter={showHover('Pending', pending)}
          onMouseMove={moveHover('Pending', pending)}
          onMouseLeave={() => setHover(null)}/>
      </svg>
      {hover && (
        <div className="donut-hover-tooltip" style={{ left: hover.x + 14, top: hover.y - 14 }}>
          {hover.label}: {hover.value.toLocaleString()}
        </div>
      )}
      <div className="donut-legend-row">
        <div className="donut-leg-item-row">
          <span className="donut-leg-dot" style={{background:'#fcd34d'}}/>
          <span>Pending ({pending})</span>
        </div>
        <div className="donut-leg-item-row">
          <span className="donut-leg-dot" style={{background:'#34d399'}}/>
          <span>Completed ({completed})</span>
        </div>
      </div>
    </div>
  );
};

function externalBreakdownTooltip(context, breakdownRows) {
  const { chart, tooltip } = context;
  let el = chart.canvas.parentNode.querySelector('.ct-tooltip');
  if (!el) {
    el = document.createElement('div');
    el.className = 'ct-tooltip';
    chart.canvas.parentNode.appendChild(el);
  }
  if (tooltip.opacity === 0) { el.style.opacity = 0; return; }

  const idx = tooltip.dataPoints?.[0]?.dataIndex;
  if (idx === undefined || !breakdownRows[idx]) { el.style.opacity = 0; return; }
  const point = breakdownRows[idx];

  const rows = point.rows.map(r =>
    `<div class="ct-row" style="color:${r.color}">${r.label}: ${r.value}</div>`
  ).join('');
  el.innerHTML = `<div class="ct-title">${point.label}</div>${rows}`;

  const { offsetLeft, offsetTop, offsetWidth } = chart.canvas;
  el.style.opacity = 1;

  const estWidth = 170;
  let left = offsetLeft + tooltip.caretX + 12;
  if (left + estWidth > offsetLeft + offsetWidth) {
    left = offsetLeft + tooltip.caretX - estWidth - 12;
  }
  el.style.left = left + 'px';

  const estHeight = 34 + point.rows.length * 20;
  const hasRoomAbove = tooltip.caretY - estHeight - 10 >= 0;
  if (hasRoomAbove) {
    el.classList.remove('below');
    el.style.top = (offsetTop + tooltip.caretY - 10) + 'px';
  } else {
    el.classList.add('below');
    el.style.top = (offsetTop + tooltip.caretY + 10) + 'px';
  }
}

const LineChart = ({ data, color, showCost = false, costPerItem = 0, chartId, breakdown = null, lineWidth = 1.8, curveTension = 0.4, pointRadius = 2, pointHoverRadius = 5 }) => {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const initChart = () => {
      if (!canvasRef.current) return;
      if (typeof window.Chart !== 'function') return;
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
      const existing = window.Chart?.getChart(canvasRef.current);
      if (existing) existing.destroy();
      const ctx = canvasRef.current.getContext('2d');
    const grad = ctx.createLinearGradient(0, 0, 0, 150);
    grad.addColorStop(0, color + '44');
    grad.addColorStop(1, color + '05');

    const isLight = document.body.classList.contains('light-mode');
    const tickColor = isLight ? 'rgba(15,23,42,0.45)' : 'rgba(255,255,255,0.3)';
    const gridColor = isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.08)';
    const maxCount = Math.max(...data.map(d => d.count), 0);

    const tooltipPlugin = breakdown
      ? {
          enabled: false,
          external: (context) => externalBreakdownTooltip(context, breakdown)
        }
      : {
          backgroundColor: 'rgba(8,18,36,0.96)',
          titleColor: 'rgba(255,255,255,0.5)',
          bodyColor: color,
          bodyFont: { size: 13, weight: '600' },
          padding: 8, cornerRadius: 7, displayColors: false,
          callbacks: {
            label: item => showCost
              ? `${item.raw} calls · $${(item.raw * costPerItem).toFixed(2)}`
              : `${item.raw} reports`
          }
        };

    chartRef.current = new window.Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.label),
        datasets: [{
          data: data.map(d => d.count),
          borderColor: color,
          borderWidth: lineWidth,
          backgroundColor: grad,
          fill: true,
          tension: curveTension,
          pointRadius,
          pointHoverRadius,
          pointBackgroundColor: color,
          pointBorderWidth: 0,
      
          pointHoverBackgroundColor: color,
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        layout: { padding: { top: 10 } },
        interaction: { mode: 'nearest', axis: 'x', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: tooltipPlugin
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: { color: tickColor, font: { size: 9 }, maxTicksLimit: 8, maxRotation: 0 }
          },
          y: {
            min: 0,
         
            suggestedMax: Math.max(Math.ceil(maxCount * 1.2), 4),
            grid: { color: gridColor, borderDash: [3, 3] },
            border: { display: false },
            ticks: { color: tickColor, font: { size: 9 }, maxTicksLimit: 4, precision: 0, stepSize: 1 }
          }
        }
      },
  
      plugins: [{
        id: 'hoverCrosshair',
        afterDraw: (c) => {
          const active = c.getActiveElements?.() || [];
          if (!active.length) return;
          const { ctx, chartArea } = c;
          const x = active[0].element.x;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, chartArea.top);
          ctx.lineTo(x, chartArea.bottom);
          ctx.lineWidth = 1;
          ctx.strokeStyle = isLight ? 'rgba(15,23,42,0.15)' : 'rgba(255,255,255,0.15)';
          ctx.stroke();
          ctx.restore();
        }
      }]
    });
      return chartRef.current;
    };

    if (window.Chart) {
      initChart();
    } else {
      const existing = document.getElementById('chartjs-cdn');
      if (!existing) {
        const s = document.createElement('script');
        s.id = 'chartjs-cdn';
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
        s.onload = initChart;
        document.head.appendChild(s);
      } else {
        existing.addEventListener('load', initChart);
      }
    }

    const observer = new MutationObserver(() => {
      if (typeof window.Chart === 'function') initChart();
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
      const el = canvasRef.current?.parentNode?.querySelector('.ct-tooltip');
      if (el) el.remove();
    };
  }, [data, color, breakdown, lineWidth, curveTension, pointRadius, pointHoverRadius]);

  return (
    <div className="chart-canvas-wrap">
      <canvas ref={canvasRef} id={chartId}
        role="img" aria-label="Report analysis chart" style={{display:'block',width:'100%',height:'100%'}}/>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats,    setStats]    = useState({ total:0, completed:0, pending:0 });
  const [modStats, setModStats] = useState([]);
  const [reports,  setReports]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [page,     setPage]     = useState(1);
  const [pagination, setPagination] = useState({});
  const [allReportTimestamps, setAllReportTimestamps] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [statusReports, setStatusReports] = useState([]); // [{created_at, status}]
  const [chartFilter, setChartFilter] = useState('today');

  useEffect(() => {
    Promise.all([
      reportsAPI.getAll({ page: 1, limit: PAGE_SIZE }),
      reportsAPI.getStats(),
    ]).then(([tableRes, statsRes]) => {
      setReports(tableRes.data?.data || []);
      setPagination(tableRes.data?.pagination || {});
      const s = statsRes.data;
      setStats({ total: s.total, completed: s.completed, pending: s.pending });
      setModStats(s.modalities || []);
      setAllReportTimestamps(s.recentTimestamps || []);
      setStatusReports(s.recentReports || []);
    }).catch(()=>{}).finally(()=>setLoading(false));
  }, []);

  const greet = () => { const h = new Date().getHours(); return h<12?'Good morning':h<17?'Good afternoon':'Good evening'; };
  const fmt = iso => new Date(iso).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});

  const totalCost = (stats.total*COST_PER_REPORT).toFixed(2);

  const STATUS_META = [
    { key: 'completed', label: 'Completed', color: '#34d399' },
    { key: 'pending',   label: 'Pending',   color: '#fcd34d' },
  ];

  const toRows = (label, counts) => ({
    label,
    rows: STATUS_META.map(m => ({ label: m.label, value: counts[m.key] || 0, color: m.color }))
  });

  const getChartData = () => {
    const now = new Date();
    const fmtHour = h => {
      if (h === 0)  return '12 AM';
      if (h < 12)   return `${h} AM`;
      if (h === 12) return '12 PM';
      return `${h - 12} PM`;
    };

    const buildBreakdown = (buckets, bucketOf) => {
      const counts = {}; // key -> {completed, pending}
      statusReports.forEach(r => {
        const d = new Date(r.created_at);
        const key = bucketOf(d);
        if (key === null) return;
        if (!counts[key]) counts[key] = { completed: 0, pending: 0 };
        const st = r.status === 'completed' ? 'completed' : 'pending';
        counts[key][st]++;
      });
      return buckets.map(b => {
        const c = counts[b.key] || { completed: 0, pending: 0 };
        return {
          label: b.label,
          count: c.completed + c.pending,
          breakdown: toRows(b.label, c),
        };
      });
    };

    let rows = [];

    if (chartFilter === 'today') {
      const buckets = Array.from({length:24},(_,h)=>({ key: h, label: fmtHour(h) }));
      rows = buildBreakdown(buckets, d =>
        d.toDateString() === now.toDateString() ? d.getHours() : null);
    } else if (chartFilter === 'yesterday') {
      const yest = new Date(now); yest.setDate(now.getDate()-1);
      const buckets = Array.from({length:24},(_,h)=>({ key: h, label: fmtHour(h) }));
      rows = buildBreakdown(buckets, d =>
        d.toDateString() === yest.toDateString() ? d.getHours() : null);
    } else if (chartFilter === 'monthly') {
      const daysInMonth = now.getDate();
      const buckets = Array.from({length:daysInMonth},(_,i)=>({ key: i+1, label: `${i+1}` }));
      rows = buildBreakdown(buckets, d =>
        (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) ? d.getDate() : null);
    } else if (chartFilter === 'yearly') {
      const months=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const buckets = months.map((mon,i)=>({ key: i, label: mon }));
      rows = buildBreakdown(buckets, d =>
        d.getFullYear() === now.getFullYear() ? d.getMonth() : null);
    }

    return {
      data: rows.map(r => ({ label: r.label, count: r.count })),
      breakdown: rows.map(r => r.breakdown),
    };
  };

  const { data: barData, breakdown: barBreakdown } = getChartData();
  const paged = reports;
  const totalPages = pagination.totalPages || 1;

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setLoading(true);
    reportsAPI.getAll({ page: newPage, limit: PAGE_SIZE })
      .then(r => { setReports(r.data?.data || []); setPagination(r.data?.pagination || {}); })
      .catch(()=>{}).finally(()=>setLoading(false));
  };

  return (
    <div className="dashboard-page">
      <h1 className="dash-hi">{greet()}, {user?.first_name}</h1>

      {/* Row 1: Stats */}
      <div className="dash-row1">
        <div className="stat-sm"><div className="stat-sm-lbl">Total Reports</div><div className="stat-sm-bottom"><div className="stat-sm-val">{stats.total}</div><div className="stat-sm-ico" style={{background:'rgba(52,211,153,0.12)'}}><AssignmentIcon sx={{fontSize:20,color:'#34d399'}}/></div></div></div>
        <div className="stat-sm"><div className="stat-sm-lbl">Completed</div><div className="stat-sm-bottom"><div className="stat-sm-val" style={{color:'#059669'}}>{stats.completed}</div><div className="stat-sm-ico" style={{background:'rgba(52,211,153,0.12)'}}><CheckCircleIcon sx={{fontSize:20,color:'#059669'}}/></div></div></div>
        <div className="stat-sm"><div className="stat-sm-lbl">Pending</div><div className="stat-sm-bottom"><div className="stat-sm-val" style={{color:'#d97706'}}>{stats.pending}</div><div className="stat-sm-ico" style={{background:'rgba(251,191,36,0.12)'}}><HourglassEmptyIcon sx={{fontSize:20,color:'#d97706'}}/></div></div></div>
        {/*
        <div className="stat-sm"><div className="stat-sm-lbl">CT</div><div className="stat-sm-bottom"><div className="stat-sm-val" style={{color:'#7c3aed'}}>{modStats.find(([n])=>n==='CT')?.[1]||0}</div><div className="stat-sm-ico" style={{background:'rgba(124,58,237,0.12)'}}><MedicalServicesIcon sx={{fontSize:20,color:'#7c3aed'}}/></div></div></div>
        <div className="stat-sm"><div className="stat-sm-lbl">MRI</div><div className="stat-sm-bottom"><div className="stat-sm-val" style={{color:'#0ea5e9'}}>{modStats.find(([n])=>n==='MRI')?.[1]||0}</div><div className="stat-sm-ico" style={{background:'rgba(14,165,233,0.12)'}}><MedicalServicesIcon sx={{fontSize:20,color:'#0ea5e9'}}/></div></div></div>
        <div className="stat-sm"><div className="stat-sm-lbl">X-ray</div><div className="stat-sm-bottom"><div className="stat-sm-val" style={{color:'#f59e0b'}}>{modStats.find(([n])=>n==='X-ray')?.[1]||0}</div><div className="stat-sm-ico" style={{background:'rgba(245,158,11,0.12)'}}><MedicalServicesIcon sx={{fontSize:20,color:'#f59e0b'}}/></div></div></div>
        */}
      </div>

      {/* Row 2: Charts */}
      <div className="dash-row2">
        <div className="d-card donut-card">
          <div className="d-card-title"><BarChartIcon sx={{fontSize:16}}/> Report Status</div>
          {loading ? (
            <div className="d-chart-loading">Loading…</div>
          ) : stats.total === 0 ? (
            <div className="d-chart-loading">No reports yet.</div>
          ) : (
            <CombinedDonut completed={stats.completed} pending={stats.pending} total={stats.total}/>
          )}
        </div>
        <div className="d-card chart-card">
          <div className="d-card-title-row">
            <div className="d-card-title"><BarChartIcon sx={{fontSize:16}}/> Report Analysis</div>
            <FilterDropdown value={chartFilter} onChange={setChartFilter} />
          </div>
          {loading ? (
            <div className="d-chart-loading">Loading…</div>
          ) : stats.total === 0 ? (
            <div className="d-chart-loading">No reports yet.</div>
          ) : (
            <>
              <LineChart
                data={barData}
                color="#34d399"
                chartId="chart-reports"
                breakdown={barBreakdown}
                lineWidth={2.8}
                curveTension={0.45}
                pointRadius={0}
                pointHoverRadius={5}
              />
              <div className="d-chart-legend">
                <span className="d-leg-dot" style={{background:'#34d399'}}/><span>Completed</span>
                <span className="d-leg-dot" style={{background:'#fcd34d', marginLeft:10}}/><span>Pending</span>
              </div>
            </>
          )}
        </div>
        {/*
        <div className="d-card chart-card">
          <div className="d-card-title-row">
            <div className="d-card-title"><SmartToyIcon sx={{fontSize:15}}/> Claude Usage <span className="d-card-sub">· ${totalCost}</span></div>
            <FilterDropdown value={chartFilter} onChange={setChartFilter} />
          </div>
          <LineChart data={barData} color="#a78bfa" chartId="chart-claude" showCost costPerItem={COST_PER_REPORT}/>
          <div className="d-chart-legend"><span className="d-leg-dot" style={{background:'#a78bfa'}}/><span>API calls · Haiku 4.5</span></div>
        </div>
        */}
      </div>

      {/* Row 3: Table */}
      <div className="d-card d-table-card">
        <div className="d-table-header">
          <div className="d-card-title" style={{marginBottom:0}}><AssignmentIcon sx={{fontSize:15}}/> Latest Reports</div>
          {pagination.total>0 && <span className="pg-info">Showing {Math.min(PAGE_SIZE, pagination.total)} of {pagination.total}</span>}
        </div>
        {loading ? <p className="loading-text">Loading…</p> : (
          <>
            <table className="reports-table">
              <colgroup><col/><col/><col/><col/><col/><col/><col/></colgroup>
              <thead><tr><th>#</th><th>Date</th><th>Email</th><th>Modality</th><th>Body Part</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {paged.map((r,i)=>(
                  <tr key={r.id}>
                    <td className="idx">{(page-1)*PAGE_SIZE+i+1}</td>
                    <td className="td-date">{fmt(r.created_at)}</td>
                    <td className="td-email">{r.profiles?.email||<span className="na">—</span>}</td>
                    <td>{r.modalities?.name?<span className="modality-pill">{r.modalities.name}</span>:<span className="na">—</span>}</td>
                    <td className="td-body">{r.body_parts?.name||<span className="na">—</span>}</td>
                    <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                    <td><Link to={`/reports/${r.id}`} className="view-icon-btn"><VisibilityIcon sx={{fontSize:13}}/> View</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="more-link-row"><Link to="/reports" className="more-link-btn">View all {pagination.total||stats.total} reports →</Link></div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;